/**
 * The hub's top-level navigation, fixed in code.
 *
 * WHY THIS IS NOT DATA-DRIVEN, which is a reversal of what the menu used to do.
 * The page tree is a legacy import of 1,415 rows, and its top level does not
 * describe an information architecture: 146 pages surface as roots only because
 * their real parent is hidden, several sections exist twice under one slug
 * (`hunting`, `firearm-licence`, `sport-shooting`), and the section rows carry
 * inconsistent titles (`FIREARM LICENCE` beside `Firearm Licence`). Rendering
 * that directly gave a 162-entry menu bar.
 *
 * The top level is also the part that barely changes — natshoot.co.za has run
 * the same handful of sections for years. So the sections are declared here and
 * everything beneath them stays fully dynamic: a page an admin adds under a
 * section appears in its dropdown with no code change, which is where the
 * data-driven behaviour actually earns its keep.
 *
 * WHAT `owns` IS FOR. A section claims one or more first segments of `slugFull`.
 * That indirection is what absorbs the import's duplication — Firearm Licensing
 * owns both `firearm-licence` and `firearm-licensing` without either row being
 * touched — and it is how content can be re-homed without a migration: moving
 * `nhsa-info` under a different section is a one-line edit here.
 *
 * Grouping by slug prefix rather than by `parentPageId` is deliberate: the
 * prefix survives a hidden parent, and `slugFull` is a materialised path, so it
 * cannot disagree with the URL the page is served under.
 */

export interface HubSectionDef {
  /** Stable React key and menu identity. Not shown. */
  key: string;
  /**
   * The label as it appears in the bar. Authored here rather than read from the
   * page row, because the imported titles are inconsistently cased.
   */
  label: string;
  /**
   * Where the section's own entry links. Points at a real page path when one
   * exists; sections whose landing page has not been authored yet resolve to
   * their first child instead (see `buildHubMenu`).
   */
  slug: string;
  /** `slugFull` first segments whose pages hang under this section. */
  owns: string[];
}

/**
 * Mirrors the public site's menu, minus News — the hub carries News, Articles
 * and Newsletters as first-class sections of its own, ahead of these.
 *
 * `nhsa-info` is the largest branch in the import (401 rows, 19 of them
 * currently visible) and has no public equivalent; it sits under Natshoot for
 * now. Some of it likely belongs under Firearm Licensing, Dedicated Status and
 * Hunting instead — that redistribution is an edit to `owns`, not a migration.
 *
 * Deliberately unclaimed: `dedicated-status-old` (superseded by
 * `dedicated-status`) and `marketing` (internal). Pages under an unclaimed
 * prefix stay reachable by URL and through search; they are simply not in the
 * nav.
 */
export const HUB_SECTIONS: HubSectionDef[] = [
  {
    key: 'natshoot',
    label: 'Natshoot',
    slug: 'natshoot',
    owns: ['natshoot', 'nhsa-info', 'nhsa-codes-and-policies'],
  },
  {
    // `slug` is the older `firearm-licence` on purpose: it holds 27 of this
    // section's 29 pages, while the tidier-looking `firearm-licensing` row has
    // one. The section's own prefix is the one flattened into the dropdown, so
    // naming the larger of the two puts the real content at the top level and
    // leaves the stray as a small group beside it.
    key: 'firearm-licensing',
    label: 'Firearm Licensing',
    slug: 'firearm-licence',
    owns: ['firearm-licence', 'firearm-licensing'],
  },
  {
    key: 'dedicated-status',
    label: 'Dedicated Status',
    slug: 'dedicated-status',
    owns: ['dedicated-status'],
  },
  {
    key: 'hunting',
    label: 'Hunting',
    slug: 'hunting',
    owns: ['hunting'],
  },
  {
    key: 'sport-shooting',
    label: 'Sport Shooting',
    slug: 'sport-shooting',
    owns: ['sport-shooting', 'training'],
  },
];

/**
 * Which section a page belongs to, by the first segment of its `slugFull`.
 *
 * The same `owns` lookup `buildHubMenu` performs, exposed for callers that hold a
 * page and need its section — the reference rail beside a content page. Returns
 * undefined for a page under an unclaimed prefix (`marketing`,
 * `dedicated-status-old`): those stay reachable by URL but are deliberately out
 * of the nav, and they get no rail for the same reason.
 */
export const findSectionKey = (
  slugFull: string,
  sections: HubSectionDef[] = HUB_SECTIONS,
): string | undefined => {
  const first = (slugFull || '').split('/').filter(Boolean)[0];
  return first ? sections.find((section) => section.owns.includes(first))?.key : undefined;
};
